import {
  AfterViewInit, Component, ElementRef, OnDestroy, OnInit, inject, signal,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { AssetService, SpaceAvailabilityRecord } from '../../services/asset.service';
import { Space, ScheduleException } from '../../models/asset.model';
import { CALENDAR_BLOCKING_STATUSES } from '../../models/booking.model';
import { SpacePublicRouteService } from '../../services/space-public-route.service';
import { BOOKING_MAX_MONTHS } from '../../shared/schedule/schedule.constants';
import { bookableMinDay, isBookableDay } from '../../shared/schedule/booking-window.utils';
import { computeDayAvailabilityStatus } from '../../shared/schedule/schedule-occupancy.utils';

// ── Types ─────────────────────────────────────────────────────────────────────

type DayStatus = 'available' | 'partial' | 'full' | 'past' | 'padding';

interface CalendarDay {
  date:    string;
  dayNum:  number;
  isToday: boolean;
  status:  DayStatus;
}

interface CalendarMonth {
  year:  number;
  month: number;
  label: string;
  days:  CalendarDay[];
}

// ── Component ─────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-space-embed-calendar',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './space-embed-calendar.component.html',
})
export class SpaceEmbedCalendarComponent implements OnInit, AfterViewInit, OnDestroy {
  private route        = inject(ActivatedRoute);
  private assetSvc     = inject(AssetService);
  private spaceRoutes  = inject(SpacePublicRouteService);
  private document     = inject(DOCUMENT);
  private host         = inject(ElementRef<HTMLElement>);

  readonly loading     = signal(true);
  readonly error       = signal<string | null>(null);
  readonly space       = signal<Space | null>(null);
  readonly months      = signal<CalendarMonth[]>([]);

  private resizeObserver?: ResizeObserver;

  readonly DOW = ['LU', 'MA', 'MI', 'JU', 'VI', 'SÁ', 'DO'] as const;

  private readonly _today            = new Date();
  private readonly _todayStr         = this._dateStr(this._today);
  private readonly _firstBookableStr = this._dateStr(bookableMinDay());

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error.set('Espacio no encontrado.');
      this.loading.set(false);
      return;
    }
    void this._load(id);
  }

  ngAfterViewInit(): void {
    const win = this.document.defaultView;
    if (!win || typeof ResizeObserver === 'undefined') {
      queueMicrotask(() => this._notifyParentHeight());
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this._notifyParentHeight());
    this.resizeObserver.observe(this.host.nativeElement);
    this._notifyParentHeight();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private async _load(id: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const space = await this.assetSvc.getSpaceById(id);
      if (!space) {
        this.error.set('Espacio no encontrado.');
        return;
      }
      this.space.set(space);
      this.months.set(this._buildCalendar());

      const [records, exceptions] = await Promise.all([
        this.assetSvc.getAvailabilityForSpace(id),
        this.assetSvc.getScheduleExceptions(id).catch(() => [] as ScheduleException[]),
      ]);

      const byDate = new Map<string, SpaceAvailabilityRecord[]>();
      for (const rec of records) {
        if (!CALENDAR_BLOCKING_STATUSES.includes(rec.status)) continue;
        const list = byDate.get(rec.date) ?? [];
        list.push(rec);
        byDate.set(rec.date, list);
      }

      this.months.update(months =>
        months.map(m => ({
          ...m,
          days: m.days.map(day => {
            if (!day.date || day.status === 'past' || day.status === 'padding') return day;
            const recs = byDate.get(day.date) ?? [];
            return { ...day, status: computeDayAvailabilityStatus(space, day.date, recs, exceptions) };
          }),
        })),
      );
    } catch {
      this.error.set('No se pudo cargar la disponibilidad. Inténtalo de nuevo.');
    } finally {
      this.loading.set(false);
    }
  }

  // ── Calendar skeleton ──────────────────────────────────────────────────────

  /** Always exactly BOOKING_MAX_MONTHS (6) month grids: current month + next 5. */
  private _buildCalendar(): CalendarMonth[] {
    return Array.from({ length: BOOKING_MAX_MONTHS }, (_, i) => {
      const d = new Date(this._today.getFullYear(), this._today.getMonth() + i, 1);
      return this._buildMonth(d.getFullYear(), d.getMonth());
    });
  }

  private _buildMonth(year: number, month: number): CalendarMonth {
    const raw   = new Date(year, month, 1)
      .toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    const label = raw.charAt(0).toUpperCase() + raw.slice(1);
    const dow0  = new Date(year, month, 1).getDay();
    const pad   = dow0 === 0 ? 6 : dow0 - 1;
    const last  = new Date(year, month + 1, 0).getDate();
    const days: CalendarDay[] = [
      ...Array.from({ length: pad }, () =>
        ({ date: '', dayNum: 0, isToday: false, status: 'padding' as DayStatus })),
      ...Array.from({ length: last }, (_, d) => {
        const dayNum = d + 1;
        const date   = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
        return {
          date, dayNum,
          isToday: date === this._todayStr,
          status: date < this._firstBookableStr ? ('past' as DayStatus) : ('available' as DayStatus),
        };
      }),
    ];
    return { year, month, label, days };
  }

  private _parseDate(dateStr: string): Date {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1);
  }

  private _dateStr(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  // ── Interacción ────────────────────────────────────────────────────────────

  onDayClick(day: CalendarDay): void {
    if (day.status !== 'available' && day.status !== 'partial') return;
    if (!isBookableDay(this._parseDate(day.date))) return;
    const space = this.space();
    if (!space?.id) return;

    const win = this.document.defaultView;
    const origin = win?.location.origin ?? 'https://timeflexrent.com';
    const bookingUrl = `${origin}/espacio/${space.id}/reservar?date=${day.date}`;

    this.spaceRoutes.rememberPendingBookingSpace(space.id);

    win?.parent?.postMessage(
      { type: 'timeflexrent:day-selected', spaceId: space.id, date: day.date, bookingUrl },
      '*',
    );

    win?.open(bookingUrl, '_blank', 'noopener');
  }

  private _notifyParentHeight(): void {
    const win = this.document.defaultView;
    if (!win?.parent || win.parent === win) return;
    const height = Math.ceil(this.host.nativeElement.scrollHeight);
    win.parent.postMessage({ type: 'timeflexrent:resize', height }, '*');
  }

  retry(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) void this._load(id);
  }
}
